import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ScanLine, Loader2, Globe, CheckCircle2, AlertTriangle } from "lucide-react";
import { http, errMsg } from "../../lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";

const KitRow = ({ r, ready }) => (
  <li data-testid={`check-all-${ready ? "ready" : "fail"}-${r.id}`} className="flex items-start gap-3 py-2 border-t border-stone-100 text-sm">
    {ready ? <CheckCircle2 size={15} className="text-emerald-600 mt-0.5 shrink-0" /> : <AlertTriangle size={15} className="text-amber-600 mt-0.5 shrink-0" />}
    <div className="min-w-0">
      <Link to={`/kits/${r.id}`} className="text-[#2B2A26] hover:text-[#9A7B2C] font-medium">{r.destination}</Link>
      <span className="text-xs text-stone-400 ml-2">{r.collection === "founders" ? "Founder's" : r.collection} · {r.edition}{r.auto_fixed ? " · auto-fixed" : ""}</span>
      {!ready && <div className="text-xs text-stone-500 mt-0.5">{r.problem}</div>}
    </div>
  </li>
);

const PublishReady = ({ ready, reload }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await http.post("/kits-bulk/publish", { ids: ready.map((r) => r.id) });
      toast.success(`Published ${data.published.length} kit(s)${data.skipped.length ? ` · ${data.skipped.length} skipped` : ""}`);
      data.skipped.forEach((s) => toast.warning(`${s.destination}: ${s.problem}`));
      reload();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <>
      <button data-testid="publish-ready-button" disabled={!ready.length || busy} onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 rounded-sm bg-emerald-700 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-800 transition-colors disabled:opacity-40">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Globe size={15} />} Publish {ready.length} ready kit(s)
      </button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="publish-ready-dialog" className="bg-white text-[#2B2A26]">
          <AlertDialogHeader>
            <AlertDialogTitle>Publish {ready.length} kit(s) to pbtravelangel.com?</AlertDialogTitle>
            <AlertDialogDescription>{ready.map((r) => r.destination).join(" · ")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="publish-ready-cancel" className="bg-white">Not yet</AlertDialogCancel>
            <AlertDialogAction data-testid="publish-ready-confirm" onClick={go} className="bg-emerald-700 text-white hover:bg-emerald-800">Publish all</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export const CheckAllPanel = () => {
  const [st, setSt] = useState(null);
  const load = useCallback(() => http.get("/kits-bulk/check-all").then((r) => setSt(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!st?.running) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [st?.running, load]);

  const start = async () => {
    try {
      await http.post("/kits-bulk/check-all");
      toast.message("Re-checking every kit page by page — light pages are filled and spills are fitted automatically");
      setTimeout(load, 1000);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const res = st?.results || [];
  const ready = res.filter((r) => r.ready);
  const fails = res.filter((r) => !r.ready && !r.live);

  return (
    <div data-testid="check-all-panel" className="rounded-xl border border-stone-200 bg-white p-6 mb-10 rise">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="eyebrow">Recheck all kits</div>
          <div data-testid="check-all-status" className="text-sm text-stone-600 mt-1">
            {st?.running ? `${st.current || "Working…"} · ${st.done || 0} of ${st.total || 0} kits`
              : st?.interrupted ? `The last run stopped at ${st.done || 0} of ${st.total || 0} kits (the server restarted) — press Check all kits to finish it.`
              : st?.finished_at ? `Last run ${new Date(st.finished_at).toLocaleString()} · ${res.filter((r) => r.live).length} live · ${ready.length} ready · ${fails.length} need work`
              : "Re-checks every kit (live, finished and bespoke) page by page on the real PDF, fills light pages and fits spills with AI, then lists what's ready."}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="check-all-button" onClick={start} disabled={st?.running}
            className="inline-flex items-center gap-2 rounded-sm bg-[#C9A847] px-4 py-2 text-sm font-bold text-white hover:bg-[#B8973A] transition-colors disabled:opacity-40">
            {st?.running ? <Loader2 size={15} className="animate-spin" /> : <ScanLine size={15} />} {st?.running ? "Checking kits…" : "Recheck all kits"}
          </button>
          {!st?.running && <PublishReady ready={ready} reload={load} />}
        </div>
      </div>
      {res.length > 0 && (
        <div className="grid md:grid-cols-2 gap-6 mt-5">
          <div><div className="text-xs uppercase tracking-wider text-emerald-700 mb-1">Ready to publish ({ready.length})</div>
            <ul data-testid="check-all-ready-list">{ready.map((r) => <KitRow key={r.id} r={r} ready />)}</ul></div>
          <div><div className="text-xs uppercase tracking-wider text-amber-700 mb-1">Needs work ({fails.length})</div>
            <ul data-testid="check-all-fail-list">{fails.map((r) => <KitRow key={r.id} r={r} />)}</ul></div>
        </div>
      )}
    </div>
  );
};
